/** Code with highlighting (highlight.js, loaded on first use), diffs, and file viewers. */
import type { Hunk } from "@agora/agent-stream";
import { structuredPatch } from "diff";
import { useEffect, useMemo, useState } from "react";

const LANG: Record<string, string> = {
  ts: "typescript",
  tsx: "typescript",
  mts: "typescript",
  cts: "typescript",
  js: "javascript",
  jsx: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  json: "json",
  md: "markdown",
  css: "css",
  scss: "scss",
  html: "xml",
  xml: "xml",
  svg: "xml",
  astro: "xml",
  vue: "xml",
  py: "python",
  rb: "ruby",
  go: "go",
  rs: "rust",
  java: "java",
  kt: "kotlin",
  swift: "swift",
  php: "php",
  sh: "bash",
  bash: "bash",
  zsh: "bash",
  yml: "yaml",
  yaml: "yaml",
  toml: "ini",
  ini: "ini",
  sql: "sql",
  c: "c",
  h: "c",
  cpp: "cpp",
  cs: "csharp",
  dockerfile: "dockerfile",
};

export function langOf(path: string): string | undefined {
  const base = path.split("/").pop()?.toLowerCase() ?? "";
  if (base === "dockerfile") return "dockerfile";
  return LANG[base.split(".").pop() ?? ""];
}

type Hljs = typeof import("highlight.js").default;
let hljsLoad: Promise<Hljs> | null = null;
const loadHljs = () => (hljsLoad ??= import("highlight.js/lib/common").then((m) => m.default as unknown as Hljs));

/** One highlighted line per entry; highlight.js output escapes the source, so it is safe as HTML. */
function useHighlighted(text: string, lang: string | undefined): string[] | null {
  const [lines, setLines] = useState<string[] | null>(null);
  useEffect(() => {
    let off = false;
    setLines(null);
    if (!lang || text.length > 300_000) return;
    void loadHljs().then((hljs) => {
      if (off || !hljs.getLanguage(lang)) return;
      // Highlight line by line so every line stands alone (spans never cross lines).
      const out = text.split("\n").map((l) => hljs.highlight(l, { language: lang, ignoreIllegals: true }).value);
      setLines(out);
    });
    return () => {
      off = true;
    };
  }, [text, lang]);
  return lines;
}

export function CodeView({ path, text, startLine = 1 }: { path: string; text: string; startLine?: number }) {
  const lang = langOf(path);
  const html = useHighlighted(text, lang);
  const plain = useMemo(() => text.split("\n"), [text]);
  return (
    <div className="wv-code" role="region" aria-label={`Code of ${path}`}>
      <table>
        <tbody>
          {plain.map((l, i) => (
            <tr key={i}>
              <td className="wv-ln">{startLine + i}</td>
              {html ? <td className="wv-src hljs" dangerouslySetInnerHTML={{ __html: html[i] ?? "" }} /> : <td className="wv-src">{l}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Before and after, from Claude Code's structured patch (or computed for an edit waiting for approval). */
export function DiffView({ path, hunks, created, content }: { path: string; hunks: Hunk[]; created?: boolean; content?: string }) {
  if (created || (!hunks.length && content !== undefined)) {
    return (
      <div className="wv-diff" role="region" aria-label={`New file ${path}`}>
        <p className="wv-diff-head">{created ? "New file" : "Whole file"}</p>
        <table>
          <tbody>
            {(content ?? "").split("\n").map((l, i) => (
              <tr key={i} className="add">
                <td className="wv-ln" />
                <td className="wv-ln">{i + 1}</td>
                <td className="wv-src">+{l}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="wv-diff" role="region" aria-label={`Changes to ${path}`}>
      {hunks.map((h, hi) => {
        let o = h.oldStart;
        let n = h.newStart;
        return (
          <table key={hi}>
            <tbody>
              <tr className="hunk">
                <td colSpan={3}>
                  Lines {h.oldStart} to {h.oldStart + Math.max(h.oldLines - 1, 0)} become {h.newStart} to {h.newStart + Math.max(h.newLines - 1, 0)}
                </td>
              </tr>
              {h.lines.map((l, i) => {
                const k = l[0];
                const row =
                  k === "+" ? (
                    <tr key={i} className="add">
                      <td className="wv-ln" />
                      <td className="wv-ln">{n++}</td>
                      <td className="wv-src">{l}</td>
                    </tr>
                  ) : k === "-" ? (
                    <tr key={i} className="del">
                      <td className="wv-ln">{o++}</td>
                      <td className="wv-ln" />
                      <td className="wv-src">{l}</td>
                    </tr>
                  ) : k === "\\" ? null : (
                    <tr key={i}>
                      <td className="wv-ln">{o++}</td>
                      <td className="wv-ln">{n++}</td>
                      <td className="wv-src">{l}</td>
                    </tr>
                  );
                return row;
              })}
            </tbody>
          </table>
        );
      })}
    </div>
  );
}

/** The diff of an edit Claude asks to make, computed here from its old and new text. */
export function proposedHunks(oldText: string, newText: string): Hunk[] {
  return structuredPatch("a", "b", oldText, newText, "", "", { context: 3 }).hunks.map((h) => ({ oldStart: h.oldStart, oldLines: h.oldLines, newStart: h.newStart, newLines: h.newLines, lines: h.lines }));
}

const b64ToBlob = (data: string, mime: string) => {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

function useBlobUrl(data: string, mime: string): string {
  const url = useMemo(() => URL.createObjectURL(b64ToBlob(data, mime)), [data, mime]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return url;
}

/** Any file the session opened: code, an image or a PDF. */
export function FileView({ path, mime, data, size }: { path: string; mime: string; data: string; size: number }) {
  if (mime.startsWith("image/")) return <ImageFile path={path} mime={mime} data={data} />;
  if (mime === "application/pdf") return <PdfFile path={path} data={data} />;
  let text = "";
  try {
    text = new TextDecoder().decode(Uint8Array.from(atob(data), (c) => c.charCodeAt(0)));
  } catch {
    text = "";
  }
  if (/\u0000/.test(text.slice(0, 2000))) return <p className="wv-muted">{path} is a binary file ({Math.round(size / 1024)} KB).</p>;
  return <CodeView path={path} text={text} />;
}

function ImageFile({ path, mime, data }: { path: string; mime: string; data: string }) {
  const url = useBlobUrl(data, mime);
  return (
    <div className="wv-image">
      <img src={url} alt={path} />
    </div>
  );
}

function PdfFile({ path, data }: { path: string; data: string }) {
  const url = useBlobUrl(data, "application/pdf");
  return (
    <object className="wv-pdf" data={url} type="application/pdf" aria-label={path}>
      <p>
        This browser does not show PDFs here.{" "}
        <a href={url} download={path.split("/").pop()}>
          Download {path.split("/").pop()}
        </a>
      </p>
    </object>
  );
}
